import React, { useState, useRef, useEffect } from "react";
import { HiXMark, HiChevronDown, HiPlus } from "react-icons/hi2";
import { ActivityType, ActivityVariable } from "../../types/activity";
import { ActivityMeasurement } from "../../types/post";
import { CreateActivityTypeDialog } from "./";
import {
  normalizeActivityVariables,
  getUnitsForVariable,
} from "../../utils/unitUtils";

interface AddMeasurementModalProps {
  isOpen: boolean;
  onClose: () => void;
  activityTypes: ActivityType[];
  onMeasurementCreated: (
    measurement: Omit<ActivityMeasurement, "timeLogged" | "activityIndex">,
  ) => void;
  onActivityTypesUpdated: () => void;
}

const AddMeasurementModal: React.FC<AddMeasurementModalProps> = ({
  isOpen,
  onClose,
  activityTypes,
  onMeasurementCreated,
  onActivityTypesUpdated,
}) => {
  // Activity selection state
  const [selectedActivityTypeId, setSelectedActivityTypeId] =
    useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showActivityDropdown, setShowActivityDropdown] =
    useState<boolean>(false);
  const [lastSelectedActivityId, setLastSelectedActivityId] =
    useState<string>("");
  const [isCreateActivityDialogOpen, setIsCreateActivityDialogOpen] =
    useState<boolean>(false);

  // Form state - variable values
  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    {},
  );
  const [selectedUnits, setSelectedUnits] = useState<Record<string, string>>(
    {},
  );
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Refs for dropdown click outside detection
  const activityDropdownRef = useRef<HTMLDivElement>(null);

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedActivityTypeId("");
      setSearchTerm("");
      setShowActivityDropdown(false);
      setLastSelectedActivityId("");
      setVariableValues({});
      setSelectedUnits({});
      setIsCreateActivityDialogOpen(false);
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Reset variable values when activity type changes
  useEffect(() => {
    if (selectedActivityTypeId) {
      const activityType = activityTypes.find(
        (at) => at.id === selectedActivityTypeId,
      );
      if (activityType) {
        const normalizedVariables = normalizeActivityVariables(
          activityType.variables,
        );
        const initialValues: Record<string, string> = {};
        const initialUnits: Record<string, string> = {};

        normalizedVariables.forEach((variable) => {
          initialValues[variable.name] = "";
          initialUnits[variable.name] = variable.defaultUnit;
        });

        setVariableValues(initialValues);
        setSelectedUnits(initialUnits);
      }
    }
  }, [selectedActivityTypeId, activityTypes]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        activityDropdownRef.current &&
        !activityDropdownRef.current.contains(event.target as Node)
      ) {
        setShowActivityDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter activity types based on search term and prioritize last selected
  const filteredActivityTypes = activityTypes
    .filter((activityType) => {
      const searchLower = searchTerm.toLowerCase();

      return activityType.name.toLowerCase().includes(searchLower);
    })
    .sort((a, b) => {
      // Put last selected activity at the top
      if (a.id === lastSelectedActivityId) return -1;
      if (b.id === lastSelectedActivityId) return 1;
      // Otherwise maintain alphabetical order
      return a.name.localeCompare(b.name);
    });

  // Get selected activity type
  const selectedActivityType = activityTypes.find(
    (type) => type.id === selectedActivityTypeId,
  );

  // Handle activity search input change
  const handleActivitySearchChange = (value: string) => {
    setSearchTerm(value);
    setShowActivityDropdown(true);
    setSelectedActivityTypeId("");
    setVariableValues({});
  };

  // Handle activity selection from dropdown
  const handleActivitySelect = (activityType: ActivityType) => {
    setSelectedActivityTypeId(activityType.id!);
    setSearchTerm(activityType.name);
    setShowActivityDropdown(false);
    setLastSelectedActivityId(activityType.id!);
  };

  // Handle activity input focus to show dropdown
  const handleActivityInputFocus = () => {
    setShowActivityDropdown(true);
  };

  // Handle create new activity
  const handleCreateActivity = () => {
    setShowActivityDropdown(false);
    setIsCreateActivityDialogOpen(true);
  };

  // Handle activity type created
  const handleActivityTypeCreated = async (
    activityTypeId: string,
    activityTypeName: string,
  ) => {
    onActivityTypesUpdated();

    setTimeout(() => {
      const newActivity = activityTypes.find(
        (activity) => activity.id === activityTypeId,
      );
      if (newActivity) {
        setSelectedActivityTypeId(newActivity.id!);
        setSearchTerm(newActivity.name);
        setLastSelectedActivityId(newActivity.id!);
      } else {
        setSelectedActivityTypeId(activityTypeId);
        setSearchTerm(activityTypeName);
        setLastSelectedActivityId(activityTypeId);
      }
      setShowActivityDropdown(false);
    }, 100);
  };

  const handleVariableChange = (variableName: string, value: string) => {
    setVariableValues((prev) => ({
      ...prev,
      [variableName]: value,
    }));
    setError(null);
  };

  const handleUnitChange = (variableName: string, unit: string) => {
    setSelectedUnits((prev) => ({
      ...prev,
      [variableName]: unit,
    }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!selectedActivityTypeId || !selectedActivityType) {
      setError("Please select an exercise");
      return;
    }

    // Build variables array with measurements and units
    const normalizedVariables = normalizeActivityVariables(
      selectedActivityType.variables,
    );
    const variables: ActivityVariable[] = normalizedVariables.map(
      (variable) => {
        const value = variableValues[variable.name] || "";
        const measurement = value.trim() ? parseFloat(value) : null;

        return {
          name: variable.name,
          measurement:
            measurement !== null && !isNaN(measurement) ? measurement : null,
          unit: selectedUnits[variable.name], // Include selected unit
        };
      },
    );

    // Check if at least one variable has a value
    const hasAtLeastOneValue = variables.some((v) => v.measurement !== null);
    if (!hasAtLeastOneValue) {
      setError("Please enter at least one measurement");
      return;
    }

    // Validate all entered values are valid numbers
    for (const variable of variables) {
      if (
        variableValues[variable.name].trim() !== "" &&
        variable.measurement === null
      ) {
        setError(`Please enter a valid number for ${variable.name}`);
        return;
      }
      if (variable.measurement !== null && variable.measurement < 0) {
        setError(`${variable.name} must be a positive number`);
        return;
      }
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const measurement: Omit<
        ActivityMeasurement,
        "timeLogged" | "activityIndex"
      > = {
        ActivityTypeID: selectedActivityTypeId,
        variables: variables,
      };

      onMeasurementCreated(measurement);
      setLastSelectedActivityId(selectedActivityTypeId);
      onClose();
    } catch (error) {
      console.error("Error creating measurement:", error);
      setError("Failed to create measurement");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4"
      onClick={onClose}
    >
      <div
        className="bg-base-100 rounded-xl shadow-xl max-w-2xl w-full h-[72vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-base-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-base-content font-heading">
            Add Exercise Measurement
          </h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 text-base-content/50 hover:text-base-content/80 rounded-full hover:bg-base-200 transition-colors disabled:opacity-50"
          >
            <HiXMark className="text-xl" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-4 md:p-6"
          style={{ minHeight: 0 }}
        >
          <div className="space-y-4">
            {/* Activity Type Selection */}
            <div
              className="relative"
              ref={activityDropdownRef}
              style={{ zIndex: 10 }}
            >
              <label
                htmlFor="activitySearch"
                className="block text-sm font-medium text-base-content/80 mb-2 font-heading"
              >
                Exercise
              </label>
              <div className="relative">
                <input
                  id="activitySearch"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleActivitySearchChange(e.target.value)}
                  onFocus={handleActivityInputFocus}
                  placeholder="Search or select an exercise..."
                  className="input bg-base-200 text-[16px] sm:text-sm w-full focus:outline-none focus:ring-0 focus:border-transparent border-base-200 border-2 p-4 pr-10 rounded-md font-grotesk"
                  disabled={isSubmitting}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowActivityDropdown(!showActivityDropdown)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-base-content/50 hover:text-base-content/80"
                  disabled={isSubmitting}
                >
                  <HiChevronDown className="w-5 h-5" />
                </button>
              </div>

              {/* Activity Dropdown */}
              {showActivityDropdown && (
                <div className="absolute z-[100] w-full mt-1 bg-base-100 border border-base-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                  {filteredActivityTypes.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-base-content/60 text-center font-grotesk">
                      No exercises found
                    </div>
                  ) : (
                    <>
                      {filteredActivityTypes.map((activityType) => (
                        <button
                          key={activityType.id}
                          type="button"
                          onClick={() => handleActivitySelect(activityType)}
                          className="w-full text-left px-4 py-3 text-sm text-base-content hover:bg-base-200 focus:bg-base-200 focus:outline-none font-grotesk"
                          disabled={isSubmitting}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-base-content font-grotesk">
                                {activityType.name}
                              </span>
                              {activityType.id === lastSelectedActivityId && (
                                <span className="text-xs px-2 py-0.5 bg-blue-500/15 text-blue-700 rounded-full">
                                  Last used
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                      <div className="border-t border-base-200">
                        <button
                          type="button"
                          onClick={handleCreateActivity}
                          className="w-full text-left px-4 py-3 text-sm text-blue-600 bg-blue-500/10 hover:bg-blue-500/15 focus:bg-blue-500/15 focus:outline-none flex items-center gap-2 font-grotesk"
                          disabled={isSubmitting}
                        >
                          <HiPlus className="w-4 h-4" />
                          Create New Exercise
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Measurement Input - Only show if activity is selected */}
            {selectedActivityType && (
              <>
                {normalizeActivityVariables(selectedActivityType.variables).map(
                  (variable) => {
                    const availableUnits = getUnitsForVariable(variable.name);
                    const currentUnit =
                      selectedUnits[variable.name] || variable.defaultUnit;

                    return (
                      <div key={variable.name}>
                        <label
                          htmlFor={`variable-${variable.name}`}
                          className="block text-sm font-medium text-base-content/80 mb-2 font-heading"
                        >
                          {variable.name}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id={`variable-${variable.name}`}
                            type="number"
                            step="0.01"
                            min="0"
                            value={variableValues[variable.name] || ""}
                            onChange={(e) =>
                              handleVariableChange(
                                variable.name,
                                e.target.value,
                              )
                            }
                            placeholder={`Enter ${variable.name.toLowerCase()}`}
                            className="input bg-base-200 text-[16px] sm:text-sm flex-1 focus:outline-none focus:ring-0 focus:border-transparent border-base-200 border-2 p-4 rounded-md font-grotesk"
                            disabled={isSubmitting}
                          />
                          <select
                            value={currentUnit}
                            onChange={(e) =>
                              handleUnitChange(variable.name, e.target.value)
                            }
                            disabled={isSubmitting}
                            className="px-3 py-2 border-2 border-base-200 rounded-md bg-base-200 text-[16px] sm:text-sm focus:outline-none focus:ring-0 focus:border-transparent"
                          >
                            {availableUnits.map((unit) => (
                              <option key={unit} value={unit}>
                                {unit}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  },
                )}
              </>
            )}

            {/* Error Message */}
            {error && (
              <div className="text-red-600 text-sm bg-red-500/10 border border-red-200 rounded-lg p-2 font-grotesk">
                {error}
              </div>
            )}
          </div>
        </form>

        {/* Footer - Fixed */}
        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-base-200 flex-shrink-0 bg-base-100 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-3 text-sm font-medium text-base-content/80 bg-base-100 border border-base-300 rounded-lg hover:bg-base-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={isSubmitting || !selectedActivityTypeId}
            className="px-4 py-3 text-sm font-medium text-white bg-neutral rounded-lg hover:bg-neutral transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
          >
            {isSubmitting ? "Adding..." : "Add Measurement"}
          </button>
        </div>
      </div>

      {/* Create Activity Type Dialog */}
      <CreateActivityTypeDialog
        isOpen={isCreateActivityDialogOpen}
        onClose={() => setIsCreateActivityDialogOpen(false)}
        initialName={searchTerm}
        onActivityTypeCreated={handleActivityTypeCreated}
      />
    </div>
  );
};

export default AddMeasurementModal;
